import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, AlertTriangle, CheckCircle, XCircle, Download, RefreshCw,
  Users, Award, Calendar, Settings, TrendingUp, Loader2, Search, ArrowLeft
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN = 'admin_secret_token_2024'; // Same as other admin pages

const AdminAwardsPage = () => {
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [nominations, setNominations] = useState([]);
  const [votes, setVotes] = useState([]);
  const [filteredVotes, setFilteredVotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stats, setStats] = useState({
    total_nominations: 0,
    qualified_nominations: 0,
    total_votes: 0,
    suspicious_votes: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterVotes();
  }, [votes, searchQuery, selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch nominations
      const nomResponse = await fetch(`${BACKEND_URL}/api/awards/admin/nominations`);
      const nomData = await nomResponse.json();
      
      if (nomData.success) {
        setNominations(nomData.nominations);
        
        // Calculate stats
        const qualified = nomData.nominations.filter(n => n.is_qualified).length;
        setStats(prev => ({
          ...prev,
          total_nominations: nomData.count,
          qualified_nominations: qualified
        }));
      }

      // Fetch votes
      const votesResponse = await fetch(`${BACKEND_URL}/api/awards/admin/votes`);
      const votesData = await votesResponse.json();
      
      if (votesData.success) {
        setVotes(votesData.votes);
        setFilteredVotes(votesData.votes);
        
        const suspicious = votesData.votes.filter(v => v.is_suspicious).length;
        setStats(prev => ({
          ...prev,
          total_votes: votesData.count,
          suspicious_votes: suspicious
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load awards data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const filterVotes = () => {
    let filtered = [...votes];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(v => v.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(v => 
        v.nominee_name.toLowerCase().includes(query) ||
        v.voter_name.toLowerCase().includes(query) ||
        v.voter_linkedin_id.toLowerCase().includes(query)
      );
    }
    
    setFilteredVotes(filtered);
  };

  const handleDisqualifyVote = async (voteId, reason) => {
    if (!window.confirm(`Disqualify this vote? Reason: ${reason}`)) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/awards/admin/vote/${voteId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: reason,
            admin_id: 'admin_user'
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Vote Disqualified',
          description: 'Vote has been removed and counts updated'
        });
        fetchData(); // Refresh data
      } else {
        throw new Error(data.message || 'Failed to disqualify vote');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleExportWinners = async (year) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/admin/export/${year}`);
      const data = await response.json();
      
      if (data.success) {
        // Convert to CSV
        const csv = [
          Object.keys(data.winners[0]).join(','),
          ...data.winners.map(w => Object.values(w).join(','))
        ].join('\n');
        
        // Download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `awards_winners_${year}.csv`;
        a.click();
        
        toast({
          title: 'Export Successful',
          description: `Winners data for ${year} downloaded`
        });
      }
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'rookie', label: 'Rookie' },
    { value: 'emerging_leader', label: 'Emerging Leader' },
    { value: 'excellence', label: 'Excellence' },
    { value: 'manager', label: 'Manager' },
    { value: 'senior_leader', label: 'Senior Leader' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50 py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="outline"
            className="mb-4 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Dashboard
          </Button>
          
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-10 h-10 text-purple-600" />
            <div>
              <h1 className="text-4xl font-bold text-slate-900">Awards Management</h1>
              <p className="text-slate-600">Manage nominations, votes, and winners</p>
            </div>
          </div>

          <Button onClick={fetchData} disabled={loading} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-semibold">Total Nominations</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_nominations}</p>
                </div>
                <Users className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-semibold">Qualified</p>
                  <p className="text-3xl font-bold text-green-600">{stats.qualified_nominations}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-semibold">Total Votes</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.total_votes}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700 font-semibold">Suspicious Votes</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.suspicious_votes}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="nominations" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="nominations">Nominations</TabsTrigger>
            <TabsTrigger value="votes">Votes Review</TabsTrigger>
            <TabsTrigger value="winners">Winners</TabsTrigger>
          </TabsList>

          {/* Nominations Tab */}
          <TabsContent value="nominations">
            <Card>
              <CardHeader>
                <CardTitle>All Nominations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Nominee</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Category</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Votes</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Qualified</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {nominations.map((nom) => (
                        <tr key={nom.nomination_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">{nom.nominee_name}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{nom.category}</Badge>
                          </td>
                          <td className="px-4 py-3 text-center font-bold text-purple-600">
                            {nom.total_votes}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={
                              nom.status === 'winner' ? 'bg-yellow-500' :
                              nom.status === 'qualified' ? 'bg-green-500' :
                              'bg-slate-500'
                            }>
                              {nom.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {nom.is_qualified ? (
                              <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                            ) : (
                              <XCircle className="w-5 h-5 text-slate-400 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Votes Tab */}
          <TabsContent value="votes">
            <Card>
              <CardHeader>
                <CardTitle>Votes Review</CardTitle>
                <div className="flex gap-4 mt-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search by nominee, voter, or LinkedIn ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 border border-slate-300 rounded-md"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Nominee</th>
                        <th className="px-3 py-2 text-left font-semibold">Voter</th>
                        <th className="px-3 py-2 text-left font-semibold">Type</th>
                        <th className="px-3 py-2 text-left font-semibold">IP</th>
                        <th className="px-3 py-2 text-center font-semibold">Status</th>
                        <th className="px-3 py-2 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredVotes.map((vote) => (
                        <tr 
                          key={vote.vote_id}
                          className={`hover:bg-slate-50 ${
                            vote.is_suspicious ? 'bg-orange-50' : ''
                          }`}
                        >
                          <td className="px-3 py-2">{vote.nominee_name}</td>
                          <td className="px-3 py-2">
                            <div className="text-xs">{vote.voter_name}</div>
                            <div className="text-xs text-slate-500">{vote.voter_linkedin_id}</div>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="text-xs">
                              {vote.voter_type}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-xs font-mono">{vote.ip_address}</td>
                          <td className="px-3 py-2 text-center">
                            {vote.is_suspicious && (
                              <AlertTriangle className="w-4 h-4 text-orange-600 mx-auto" />
                            )}
                            {!vote.is_valid && (
                              <XCircle className="w-4 h-4 text-red-600 mx-auto" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {vote.is_valid && (
                              <Button
                                onClick={() => handleDisqualifyVote(vote.vote_id, 'Fraudulent activity detected')}
                                size="sm"
                                variant="destructive"
                                className="text-xs"
                              >
                                Disqualify
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredVotes.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No votes found matching your criteria
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Winners Tab */}
          <TabsContent value="winners">
            <Card>
              <CardHeader>
                <CardTitle>Winners Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Important Actions
                  </h3>
                  <p className="text-sm text-yellow-700 mb-4">
                    These actions are permanent and should only be performed after the voting period ends.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="border-2 border-purple-200">
                    <CardContent className="pt-6">
                      <Award className="w-12 h-12 text-purple-600 mb-4" />
                      <h3 className="text-xl font-bold mb-2">Finalize Winners</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Calculate and announce winners for the current year
                      </p>
                      <Button 
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to finalize winners? This cannot be undone.')) {
                            toast({
                              title: 'Feature Coming Soon',
                              description: 'Use the backend API endpoint to finalize winners'
                            });
                          }
                        }}
                      >
                        Finalize Winners 2026
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-blue-200">
                    <CardContent className="pt-6">
                      <Download className="w-12 h-12 text-blue-600 mb-4" />
                      <h3 className="text-xl font-bold mb-2">Export Winners</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Download winners data for certificates and announcements
                      </p>
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleExportWinners(2026)}
                      >
                        Export 2026 Winners
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminAwardsPage;
